import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { CartItem } from '../types/cart';
import { useAuth } from './useAuth';

// Helper to get cart from localStorage
const getLocalCart = (): CartItem[] => {
  try {
    return JSON.parse(localStorage.getItem('cart') || '[]');
  } catch {
    return [];
  }
};

// Helper to save cart to localStorage
const saveLocalCart = (cart: CartItem[]) => {
  localStorage.setItem('cart', JSON.stringify(cart));
};

export function useCart() {
  const { user } = useAuth();
  const [items, setItems] = useState<CartItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState(0);

  // Load cart data on mount and when auth state changes
  useEffect(() => {
    async function loadCart() {
      try {
        setLoading(true);
        setError(null);

        if (user) {
          // Load cart from Supabase for authenticated users
          const { data, error } = await supabase
            .from('shopping_cart')
            .select(`
              id,
              user_id,
              product_id,
              quantity,
              created_at,
              updated_at,
              product:products!inner (
                id,
                name,
                description,
                price,
                image_url
              )
            `)
            .eq('user_id', user.id);

          // Handle the case where no items are found
          if (error) {
            // PGRST116 means no rows found, which is expected for empty cart
            if (error.code === 'PGRST116') {
              setItems([]);
              return;
            }
            throw error;
          }

          // Check if there's a local cart to merge
          const localCart = getLocalCart();
          if (localCart.length > 0) {
            // Merge local cart with server cart
            for (const item of localCart) {
              const existingItem = data?.find(i => i.product_id === item.product_id);
              if (existingItem) {
                // Update quantity if item exists
                await supabase
                  .from('shopping_cart')
                  .update({ quantity: existingItem.quantity + item.quantity })
                  .eq('id', existingItem.id);
              } else {
                // Add new item if it doesn't exist
                await supabase
                  .from('shopping_cart')
                  .insert({
                    user_id: user.id,
                    product_id: item.product_id,
                    quantity: item.quantity
                  });
              }
            }
            // Clear local cart after merging
            localStorage.removeItem('cart');
            // Fetch updated cart
            const { data: updatedData } = await supabase
              .from('shopping_cart')
              .select(`
                id,
                user_id,
                product_id,
                quantity,
                created_at,
                updated_at,
                product:products!inner (
                  id,
                  name,
                  description,
                  price,
                  image_url
                )
              `)
              .eq('user_id', user.id);
            setItems(updatedData || []);
          } else {
            setItems(data || []);
          }
        } else {
          // Load cart from localStorage for unauthenticated users
          setItems(getLocalCart());
        }
      } catch (err) {
        console.error('Error loading cart:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    }

    loadCart();
  }, [user]);

  // Update total whenever items change
  useEffect(() => {
    const cartTotal = items.reduce((sum, item) => {
      return sum + (item.product.price * item.quantity);
    }, 0);
    setTotal(cartTotal);
  }, [items]);

  const addToCart = async (productId: string) => {
    try {
      if (user) {
        // Add to Supabase cart for authenticated users
        const { data: existingItems } = await supabase
          .from('shopping_cart')
          .select('id, quantity')
          .eq('user_id', user.id)
          .eq('product_id', productId)
          .maybeSingle();

        if (existingItems) {
          // Update quantity if item exists
          const { error } = await supabase
            .from('shopping_cart')
            .update({ quantity: existingItems.quantity + 1 })
            .eq('id', existingItems.id);

          if (error) throw error;
        } else {
          // Add new item if it doesn't exist
          const { error } = await supabase
            .from('shopping_cart')
            .insert({
              user_id: user.id,
              product_id: productId,
              quantity: 1
            });

          if (error) throw error;
        }

        // Fetch updated cart
        const { data: updatedCart, error: fetchError } = await supabase
          .from('shopping_cart')
          .select(`
            id,
            user_id,
            product_id,
            quantity,
            created_at,
            updated_at,
            product:products!inner (
              id,
              name,
              description,
              price,
              image_url
            )
          `)
          .eq('user_id', user.id);

        if (fetchError) throw fetchError;
        setItems(updatedCart || []);
      } else {
        // Add to localStorage cart for unauthenticated users
        const { data: product } = await supabase
          .from('products')
          .select('*')
          .eq('id', productId)
          .single();

        if (!product) throw new Error('Product not found');

        const localCart = getLocalCart();
        const existingItem = localCart.find(item => item.product_id === productId);

        if (existingItem) {
          existingItem.quantity += 1;
        } else {
          localCart.push({
            id: crypto.randomUUID(),
            product_id: productId,
            quantity: 1,
            product: product,
            user_id: 'local',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });
        }

        saveLocalCart(localCart);
        setItems(localCart);
      }
    } catch (err) {
      throw err;
    }
  };

  const updateQuantity = async (itemId: string, quantity: number) => {
    try {
      if (quantity < 1) return;

      if (user) {
        // Update quantity in Supabase for authenticated users
        const { error } = await supabase
          .from('shopping_cart')
          .update({ quantity })
          .eq('id', itemId);

        if (error) throw error;

        // Fetch updated cart
        const { data: updatedCart } = await supabase
          .from('shopping_cart')
          .select(`
            id,
            user_id,
            product_id,
            quantity,
            created_at,
            updated_at,
            product:products!inner (
              id,
              name,
              description,
              price,
              image_url
            )
          `)
          .eq('user_id', user.id);

        setItems(updatedCart || []);
      } else {
        // Update quantity in localStorage for unauthenticated users
        const localCart = getLocalCart();
        const updatedCart = localCart.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        );
        saveLocalCart(updatedCart);
        setItems(updatedCart);
      }
    } catch (err) {
      throw err;
    }
  };

  const removeFromCart = async (itemId: string) => {
    try {
      if (user) {
        // Remove from Supabase for authenticated users
        const { error } = await supabase
          .from('shopping_cart')
          .delete()
          .eq('id', itemId);

        if (error) throw error;

        // Update local state
        setItems(prev => prev.filter(item => item.id !== itemId));
      } else {
        // Remove from localStorage for unauthenticated users
        const localCart = getLocalCart().filter(item => item.id !== itemId);
        saveLocalCart(localCart);
        setItems(localCart);
      }
    } catch (err) {
      throw err;
    }
  };

  return {
    items,
    loading,
    error,
    total,
    addToCart,
    updateQuantity,
    removeFromCart
  };
}